import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';
import { step, stringPayload, rangeIds } from './fallback-helpers';

export function stringFallback(id: string, raw: any): ExecutionStep[] {
  const text    = (raw?.text    || raw?.word1 || 'ABABDABACDABABCABAB').slice(0, 30);
  const pattern = (raw?.pattern || raw?.word2 || 'ABABCABAB').slice(0, 14);
  if (id.includes('kmp'))    return kmpString(text, pattern);
  if (id.includes('rabin'))  return rabinKarp(text, pattern);
  if (id.includes('boyer'))  return naiveString(text, pattern);
  return naiveString(text, pattern);
}

function naiveString(text: string, pattern: string): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  const t = text.slice(0, 24); const p = pattern.slice(0, 12);
  s.push(step(s.length, StepType.Initialization, stringPayload(t, { word1: t, word2: p }), { line: 1, explanation: `Naive matching: text length ${t.length}, pattern length ${p.length}.` }));
  for (let i = 0; i <= t.length - p.length; i++) {
    s.push(step(s.length, StepType.Comparison, stringPayload(t, { currentIndex: i, windowStart: i, windowEnd: i + p.length - 1, word1: t, word2: p }), { line: 3, explanation: `Align pattern at text[${i}..${i + p.length - 1}].` }));
    let matched = true;
    const matchedIndices: number[] = [];
    const mismatchIndices: number[] = [];
    for (let j = 0; j < p.length; j++) {
      const ti = i + j;
      if (t[ti] === p[j]) {
        matchedIndices.push(ti);
        s.push(step(s.length, StepType.Comparison, stringPayload(t, { currentIndex: ti, matchedIndices: [...matchedIndices], windowStart: i, windowEnd: i + p.length - 1, word1: t, word2: p }), { line: 5, explanation: `Match: text[${ti}]='${t[ti]}' == pattern[${j}]='${p[j]}'.` }));
      } else {
        matched = false;
        mismatchIndices.push(ti);
        s.push(step(s.length, StepType.Decision, stringPayload(t, { currentIndex: ti, mismatchIndices, windowStart: i, windowEnd: i + p.length - 1, word1: t, word2: p }), { line: 6, explanation: `Mismatch: text[${ti}]='${t[ti]}' != pattern[${j}]='${p[j]}'. Shift.` }));
        break;
      }
    }
    if (matched) {
      s.push(step(s.length, StepType.Update, stringPayload(t, { matchedIndices: rangeIds(i, i + p.length - 1), windowStart: i, windowEnd: i + p.length - 1, word1: t, word2: p }), { line: 7, explanation: `Pattern found at index ${i}.` }));
    }
  }
  s.push(step(s.length, StepType.Completion, stringPayload(t, { word1: t, word2: p }), { line: 9, explanation: 'Naive matching complete.' }));
  return s;
}

function kmpString(text: string, pattern: string): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  const t = text.slice(0, 24); const p = pattern.slice(0, 12);

  const fail: number[] = Array(p.length).fill(0);
  let k = 0;
  for (let i = 1; i < p.length; i++) {
    while (k > 0 && p[k] !== p[i]) k = fail[k - 1]!;
    if (p[k] === p[i]) k++;
    fail[i] = k;
  }

  s.push(step(s.length, StepType.Initialization, stringPayload(t, { word1: t, word2: p, lpsTable: [...fail] }), { line: 1, explanation: `KMP: failure table computed for pattern "${p}". LPS=[${fail.join(',')}].` }));

  let q = 0;
  const matched: number[] = [];
  for (let i = 0; i < t.length; i++) {
    s.push(step(s.length, StepType.Comparison, stringPayload(t, { currentIndex: i, windowStart: i - q, windowEnd: i, word1: t, word2: p, lpsTable: [...fail] }), { line: 4, explanation: `Scan text[${i}]='${t[i]}', q=${q} chars matched.` }));
    while (q > 0 && p[q] !== t[i]) {
      s.push(step(s.length, StepType.Decision, stringPayload(t, { currentIndex: i, mismatchIndices: [i], windowStart: i - q, windowEnd: i, word1: t, word2: p, lpsTable: [...fail] }), { line: 5, explanation: `Mismatch: fall back via failure table to q=${fail[q - 1]}.` }));
      q = fail[q - 1]!;
    }
    if (p[q] === t[i]) {
      matched.push(i);
      q++;
      s.push(step(s.length, StepType.Update, stringPayload(t, { currentIndex: i, matchedIndices: [...matched], windowStart: i - q + 1, windowEnd: i, word1: t, word2: p, lpsTable: [...fail] }), { line: 6, explanation: `Match at text[${i}]='${t[i]}', q=${q}.` }));
    }
    if (q === p.length) {
      s.push(step(s.length, StepType.Update, stringPayload(t, { matchedIndices: rangeIds(i - p.length + 1, i), windowStart: i - p.length + 1, windowEnd: i, word1: t, word2: p, lpsTable: [...fail] }), { line: 7, explanation: `Pattern found at index ${i - p.length + 1}.` }));
      q = fail[q - 1]!;
    }
  }
  s.push(step(s.length, StepType.Completion, stringPayload(t, { word1: t, word2: p, lpsTable: [...fail] }), { line: 9, explanation: 'KMP search complete.' }));
  return s;
}

function rabinKarp(text: string, pattern: string): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  const t = text.slice(0, 24); const p = pattern.slice(0, 12);
  const BASE = 31; const MOD = 1_000_003;

  const charVal = (c: string) => c.charCodeAt(0) - 96;
  const windowLen = p.length;
  let patHash = 0, pow = 1;
  for (let i = 0; i < windowLen; i++) {
    patHash = (patHash + charVal(p[i] ?? 'a') * pow) % MOD;
    if (i < windowLen - 1) pow = (pow * BASE) % MOD;
  }

  s.push(step(s.length, StepType.Initialization, stringPayload(t, { word1: t, word2: p, patternHash: patHash }), { line: 1, explanation: `Rabin-Karp: pattern hash = ${patHash}.` }));

  let winHash = 0, wp = 1;
  for (let i = 0; i < windowLen && i < t.length; i++) {
    winHash = (winHash + charVal(t[i] ?? 'a') * wp) % MOD;
    if (i < windowLen - 1) wp = (wp * BASE) % MOD;
  }

  for (let i = 0; i <= t.length - windowLen; i++) {
    const hashMatch = winHash === patHash;
    s.push(step(s.length, StepType.Comparison, stringPayload(t, { currentIndex: i, windowStart: i, windowEnd: i + windowLen - 1, windowHash: winHash, patternHash: patHash, word1: t, word2: p }), { line: 3, explanation: `Window [${i},${i + windowLen - 1}] hash=${winHash} (target=${patHash}) — ${hashMatch ? 'hash match, verify chars' : 'hash miss, slide'}.` }));
    if (hashMatch) {
      let realMatch = true;
      for (let j = 0; j < windowLen; j++) { if (t[i + j] !== p[j]) { realMatch = false; break; } }
      if (realMatch) {
        s.push(step(s.length, StepType.Update, stringPayload(t, { matchedIndices: rangeIds(i, i + windowLen - 1), windowStart: i, windowEnd: i + windowLen - 1, windowHash: winHash, patternHash: patHash, word1: t, word2: p }), { line: 5, explanation: `Pattern confirmed at index ${i}.` }));
      } else {
        s.push(step(s.length, StepType.Decision, stringPayload(t, { currentIndex: i, mismatchIndices: [i], windowStart: i, windowEnd: i + windowLen - 1, windowHash: winHash, patternHash: patHash, word1: t, word2: p }), { line: 6, explanation: `Hash collision at ${i} — chars differ.` }));
      }
    }
    if (i + windowLen < t.length) {
      winHash = (winHash - charVal(t[i] ?? 'a') * pow % MOD + MOD) % MOD;
      winHash = (winHash * BASE + charVal(t[i + windowLen] ?? 'a')) % MOD;
    }
  }
  s.push(step(s.length, StepType.Completion, stringPayload(t, { word1: t, word2: p, patternHash: patHash }), { line: 9, explanation: 'Rabin-Karp search complete.' }));
  return s;
}
